import { GraphQLQuery, GraphQLResult } from "@aws-amplify/api";
import { Monitor } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Container,
  Stack,
  TextField,
} from "@mui/material";
import { API, graphqlOperation } from "aws-amplify";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import {
  GetDeviceQueryVariables,
  UpdateDeviceInput,
  UpdateDeviceMutation,
} from "../../API";
import Loading from "../../components/Loading/Loading";
import { useData } from "../../contexts/DataProvider";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { Device } from "../../types";

/**
 * @summary - Device Edit scene. As for right now (10 mars 2023), can only update device's name and description.
 * Also using REACT HOOK FORM LIBRARY : https://react-hook-form.com/
 */

type FormInputs = {
  name: string;
  description: string;
};

const DeviceEdit = () => {
  const { clientId } = useData();
  const navigate = useNavigate();
  const { id } = useParams();

  const intl = useIntl();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isLoading, isDirty },
  } = useForm<FormInputs>({
    defaultValues: async () => await fetchDevice(),
  });

  async function updateDevice(formData: FormInputs) {
    const deviceDetails: UpdateDeviceInput = {
      clientID: clientId,
      name: formData.name,
      description: formData.description,
    };

    //TODO: Update the context devices state to reflect new changes OR use subscribtion instead of query in the context
    await API.graphql<GraphQLQuery<UpdateDeviceMutation>>({
      query: mutations.updateDevice,
      variables: { input: deviceDetails },
    });
  }

  /**
   * @summary - Fetch device by Id,
   * If Not Found, redirect NotFound,
   */
  async function fetchDevice() {
    const variables: GetDeviceQueryVariables = {
      clientID: clientId,
      name: id!,
    };

    let apiData: GraphQLResult<GraphQLQuery<Device>>;

    try {
      apiData = await API.graphql<GraphQLQuery<Device>>({
        query: queries.getDevice,

        variables: variables,
      });
    } catch (error) {
      console.error(`error fetching device`, error);
    }

    const { name, description } = apiData!.data?.getDevice!;
    return { name: name || "", description: description || "" };
  }

  const onSubmit: SubmitHandler<any> = async (formData: FormInputs) => {
    try {
      await updateDevice(formData);
      navigate(-1);
    } catch (_) {
      //do nothing because logged by error boundary
    }
  };

  if (isLoading) return <Loading />;

  return (
    <Container component="main" maxWidth="xs">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "warning.main", width: 70, height: 70 }}>
          <Monitor fontSize="large" />
        </Avatar>

        <Controller
          control={control}
          name="name"
          // rules={{
          //   required: {
          //     value: true,
          //     message: "Un nom est requis",
          //   },
          // }}
          render={({ field }) => (
            <TextField
              {...field}
              // required
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              fullWidth
              label={intl.formatMessage({
                id: "workspaceName",
                defaultMessage: "Nom",
              })}
              id="nom"
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          // rules={{
          //   required: {
          //     value: true,
          //     message: "Une description est requise",
          //   },
          // }}
          render={({ field }) => (
            <TextField
              {...field}
              // required
              margin="normal"
              fullWidth
              label="Description"
              id="description"
              error={!!errors.description}
              helperText={errors.description?.message}
            />
          )}
        />

        <Stack
          mt={2}
          width="100%"
          direction="row"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            type="button"
            variant="contained"
            onClick={() => navigate(-1)}
          >
            {intl.formatMessage({ id: "backButton", defaultMessage: "Retour" })}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={isSubmitting || !isDirty}
          >
            {intl.formatMessage({
              id: "editButton",
              defaultMessage: "Modifier",
            })}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default DeviceEdit;
